import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell } from './LoginPage';
import { humanizeError } from '@/lib/errors';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, watch, formState: { errors } } =
    useForm<{ password: string; password_confirm: string }>();
  const password = watch('password');

  if (!token) {
    return (
      <AuthShell title={t('auth.resetPassword')}>
        <div className="space-y-4">
          <p className="text-sm text-error">
            {t('auth.resetTokenInvalid')}
          </p>
          <Link to="/forgot-password" className="inline-block font-semibold text-primary">
            ← {t('auth.forgotPassword')}
          </Link>
        </div>
      </AuthShell>
    );
  }

  const onSubmit = async (data: { password: string }) => {
    setSubmitting(true);
    setError(null);
    try {
      await authApi.resetPassword(token, data.password);
      setDone(true);
    } catch (e) {
      // Backend returns 400 "invalid or expired reset token" verbatim; show
      // a clear Russian hint with a way to request a fresh link.
      const raw = (e as { message?: string })?.message ?? '';
      if (/reset token|expired|invalid/i.test(raw)) {
        setError(t('auth.resetTokenExpired'));
      } else {
        setError(humanizeError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthShell title={t('auth.resetPassword')}>
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-card bg-success-container p-4 text-on-success-container">
            <span className="material-symbols-outlined text-success">check_circle</span>
            <div className="space-y-1">
              <p className="font-semibold">{t('auth.passwordUpdated')}</p>
              <p className="text-sm">{t('auth.passwordUpdatedHint')}</p>
              {/* ⚠️ Ссылка из письма открывает САЙТ в браузере, даже если сброс просили из приложения: ссылок,
                  открывающих приложение, у нас нет (02.10.2026). Без этой строки человек оставался на входе сайта
                  и не понимал, что новый пароль уже годится в приложении. */}
              <p className="text-sm">{t('auth.passwordUpdatedApp')}</p>
            </div>
          </div>
          <Link to="/login" className="inline-block font-semibold text-primary">
            ← {t('auth.signIn')}
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('auth.resetPassword')}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          type="password"
          autoComplete="new-password"
          label={t('auth.password')}
          hint={t('auth.passwordHint')}
          {...register('password', {
            required: true,
            minLength: 8,
            validate: (v) =>
              /[A-Z]/.test(v) && /[a-z]/.test(v) && /\d/.test(v) && /[^A-Za-z0-9]/.test(v),
          })}
          error={
            errors.password &&
            (errors.password.type === 'minLength'
              ? t('auth.errorPasswordShort')
              : t('auth.errorPasswordWeak'))
          }
        />
        <Input
          type="password"
          autoComplete="new-password"
          label={t('auth.passwordConfirm')}
          {...register('password_confirm', {
            required: true,
            validate: (v) => v === password,
          })}
          error={errors.password_confirm && t('auth.errorPasswordMismatch')}
        />
        {error && (
          <div className="space-y-2">
            <p className="text-sm text-error">{error}</p>
            <Link to="/forgot-password" className="text-sm font-semibold text-primary">
              {t('auth.requestNewLink')}
            </Link>
          </div>
        )}
        <Button type="submit" size="lg" fullWidth loading={submitting}>
          {t('auth.resetPassword')}
        </Button>
      </form>
    </AuthShell>
  );
}
